<template>
	<div class="trigger">
		<section>
			<div class="title">
				<span>When</span>
				<v-spacer />
				<AddConditionMenu
					:fn-add-date="showAddDateDialog"
					:fn-add-time="showAddTimeDialog"
					:fn-add-interval="showAddIntervalDialog"
					:fn-add-or-block="addWhenOrBlock"
				/>
			</div>
			<Grid
				:nodes="whenConditions"
				:fragment="WhenFragment"
				:error-fragment="ErrorFragment"
				:fn-edit-node="showEditDialog"
				:fn-remove-node="showRemoveDialog"
				:fn-add-date="showAddDateDialog"
				:fn-add-time="showAddTimeDialog"
				:fn-add-interval="showAddIntervalDialog"
			/>
		</section>

		<section>
			<div class="title">
				<span>If</span>
				<v-spacer />
				<AddConditionMenu
					:fn-add-var-condition="showAddVarConditionDialog"
					:fn-add-event-condition="showAddEventConditionDialog"
					:fn-add-or-block="addOrBlock"
				/>
			</div>
			<Grid
				:nodes="ifConditions"
				:fragment="IfFragment"
				:error-fragment="ErrorFragment"
				:fn-edit-node="showEditDialog"
				:fn-remove-node="showRemoveDialog"
				:fn-add-var-condition="showAddVarConditionDialog"
				:fn-add-event-condition="showAddEventConditionDialog"
			/>
		</section>

		<section>
			<div class="title">
				<span>Then</span>
				<v-spacer />
				<AddStepMenu
					:fn-add-command-step="showAddCommandStepDialog"
					:fn-add-event-step="showAddEventStepDialog"
					:fn-add-delay-step="showAddDelayStepDialog"
				/>
			</div>
			<Grid
				:nodes="steps"
				:fragment="StepFragment"
				:error-fragment="ErrorFragment"
				:fn-edit-node="showEditDialog"
				:fn-move-to-top="moveToTop"
				:fn-move-up="moveUp"
				:fn-move-down="moveDown"
				:fn-move-to-bottom="moveToBottom"
				:fn-remove-node="showRemoveDialog"
			/>
		</section>

		<!-- Dialogs -->
		<DateDialog ref="dateDialog" />
		<VarConditionDialog ref="varConditionDialog" />
		<EventConditionDialog ref="eventConditionDialog" />
		<IntervalDialog ref="intervalDialog" />
		<CommandStepDialog ref="commandStepDialog" />
		<EventStepDialog ref="eventStepDialog" />
		<DelayStepDialog ref="delayStepDialog" />
		<TimeConditionDialog ref="timeDialog" />
		<dialog-confirm-dialog
			ref="confirmDeleteDialog"
			body="Are you sure you want to remove this node?"
		/>
	</div>
</template>

<style lang="scss" scoped>
.trigger {
	font-size: 0.95rem;
	padding: 12px 0 0 24px;

	.title {
		display: flex;
		margin-right: 20px;
	}
}
</style>

<script>
import _ from "lodash";
import { mapActions } from "vuex";

import Grid from "./condition/Grid";
import AddConditionMenu from "./menu/AddConditionMenu";
import AddStepMenu from "./menu/AddStepMenu";
import WhenFragment from "./condition/WhenFragment";
import IfFragment from "./condition/IfFragment";
import StepFragment from "./condition/StepFragment";
import ErrorFragment from "./condition/ErrorFragment";

import DateDialog from "./dialogs/DateConditionDialog";
import VarConditionDialog from "./dialogs/VarConditionDialog";
import EventConditionDialog from "./dialogs/EventConditionDialog";
import IntervalDialog from "./dialogs/IntervalDialog";
import CommandStepDialog from "./dialogs/CommandStepDialog";
import EventStepDialog from "./dialogs/EventStepDialog";
import DelayStepDialog from "./dialogs/DelayStepDialog";
import TimeConditionDialog from "./dialogs/TimeConditionDialog";

export default {
	components: {
		AddConditionMenu,
		AddStepMenu,
		DateDialog,
		VarConditionDialog,
		EventConditionDialog,
		IntervalDialog,
		CommandStepDialog,
		EventStepDialog,
		DelayStepDialog,
		TimeConditionDialog,
		Grid,
	},

	props: {
		trigger: {
			type: Object,
			required: true,
		},
		scene: {
			type: Object,
			required: true,
		},
	},

	data: () => ({
		WhenFragment,
		IfFragment,
		StepFragment,
		ErrorFragment,
	}),

	computed: {
		whenConditions() {
			return [].concat(
				this.trigger.intervalConditions,
				this.trigger.dateConditions,
				this.trigger.timeConditions,
				this.trigger.dateTimeOrConditions
			);
		},

		ifConditions() {
			return [].concat(this.trigger.ifConditions, this.trigger.ifOrConditions);
		},

		steps() {
			return this.scene.steps;
		},
	},

	methods: {
		...mapActions({
			_addOrBlock: "scenes/addOrBlock",
			_addCondition: "scenes/addCondition",
			_updateCondition: "scenes/updateCondition",
			_addInterval: "scenes/addInterval",
			_updateInterval: "scenes/updateInterval",
			_addStep: "scenes/addStep",
			_updateStep: "scenes/updateStep",
			_addDelayStep: "scenes/addDelayStep",
			_updateDelayStep: "scenes/updateDelayStep",
			_reorderSteps: "scenes/reorderSteps",

			_removeNode: "controller/nodes/remove",
		}),

		async showRemoveDialog(node) {
			const confirm = await this.$refs.confirmDeleteDialog.pop();
			if (confirm) return this._removeNode(node);
		},

		showAddDateDialog(parent) {
			if (parent instanceof Event) parent = this.trigger;
			const handler = _.curryRight(this.addClockCondition)(parent);
			return this.$refs.dateDialog.pop(handler);
		},

		showAddTimeDialog(parent) {
			if (parent instanceof Event) parent = this.trigger;
			const handler = _.curryRight(this.addClockCondition)(parent);
			return this.$refs.timeDialog.pop(handler);
		},

		showAddVarConditionDialog(parent) {
			if (parent instanceof Event) parent = this.trigger;
			const handler = _.curryRight(this.addVarCondition)(parent);
			return this.$refs.varConditionDialog.pop(handler);
		},

		showAddEventConditionDialog(parent) {
			if (parent instanceof Event) parent = this.trigger;
			const handler = _.curryRight(this.addEventCondition)(parent);
			return this.$refs.eventConditionDialog.pop(handler);
		},

		showAddIntervalDialog(parent) {
			if (parent instanceof Event) parent = this.trigger;
			const handler = _.curryRight(this.addInterval)(parent);
			this.$refs.intervalDialog.pop(handler);
		},

		showAddCommandStepDialog(parent) {
			return this.$refs.commandStepDialog.pop(this.addStep);
		},

		showAddEventStepDialog() {
			return this.$refs.eventStepDialog.pop(this.addStep);
		},

		showAddDelayStepDialog() {
			return this.$refs.delayStepDialog.pop(this.addDelayStep);
		},

		showEditDialog(node) {
			let handler = _.noop;
			let dialog = null;

			if (node.targetIsTime) {
				// Time
				handler = this.updateClockCondition;
				dialog = this.$refs.timeDialog;
			} else if (node.targetIsDate) {
				// Date
				handler = this.updateClockCondition;
				dialog = this.$refs.dateDialog;
			} else if (node.isInterval) {
				// Interval
				handler = this.updateInterval;
				dialog = this.$refs.intervalDialog;
			} else if (
				node.constructor.modelName === "ConditionNode" &&
				node.targetIsVariable
			) {
				// Variable condition
				handler = this.updateVarCondition;
				dialog = this.$refs.varConditionDialog;
			} else if (
				node.constructor.modelName === "ConditionNode" &&
				node.targetIsEvent
			) {
				// Event condition
				handler = this.updateEventCondition;
				dialog = this.$refs.eventConditionDialog;
			} else if (node.constructor.modelName === "StepNode") {
				handler = this.updateStep;
				if (node.targetIsEvent) {
					// Event step
					dialog = this.$refs.eventStepDialog;
				} else if (node.targetIsCommand) {
					// Command step
					dialog = this.$refs.commandStepDialog;
				}
			} else if (node.isDelay) {
				// Delay
				handler = this.updateDelayStep;
				dialog = this.$refs.delayStepDialog;
			} else {
				console.warn("Unable to edit node of this type", node);
				return;
			}

			handler = _.curryRight(handler)(node);
			return dialog.pop(handler, node);
		},

		addVarCondition({ path, operator, value }, parent) {
			return this._addCondition({
				parent,
				condition: { path, operator, value },
			});
		},

		updateVarCondition({ path, operator, value }, node) {
			return this._updateCondition({
				node,
				condition: { path, operator, value },
			});
		},

		addEventCondition({ path, operator, value }, parent) {
			return this._addCondition({
				parent,
				condition: { path, operator, value },
			});
		},

		updateEventCondition({ path, operator, value }, node) {
			return this._updateCondition({
				node,
				condition: { path, operator, value },
			});
		},

		addInterval({ order, seconds }, parent) {
			return this._addInterval({
				parent,
				interval: { order, seconds },
			});
		},

		updateInterval({ order, seconds }, node) {
			return this._updateInterval({
				node,
				interval: { order, seconds },
			});
		},

		addClockCondition({ dateType, operator, value }, parent) {
			const controller =
				this.$store.getters["controller/nodes/controller"].path;
			const path = `${controller}/variables/clock/${dateType}`;

			return this._addCondition({
				parent,
				condition: { path, operator, value },
			});
		},

		updateClockCondition({ guid, dateType, operator, value }, node) {
			const controller =
				this.$store.getters["controller/nodes/controller"].path;
			const path = `${controller}/variables/clock/${dateType}`;

			return this._updateCondition({
				node,
				condition: { guid, path, operator, value },
			});
		},

		addWhenOrBlock() {
			return this._addOrBlock({ trigger: this.trigger, when: true });
		},

		addOrBlock() {
			return this._addOrBlock({ trigger: this.trigger });
		},

		// For adding either command or event steps - they both use the same arguments
		addStep({ path, order, args }) {
			return this._addStep({
				scene: this.scene,
				step: { path, order, args },
			});
		},

		updateStep({ path, order, args }, node) {
			return this._updateStep({
				node,
				step: { path, order, args },
			});
		},

		addDelayStep({ order, seconds }) {
			return this._addDelayStep({
				scene: this.scene,
				step: { seconds, order },
			});
		},

		updateDelayStep({ order, seconds }, node) {
			return this._updateDelayStep({
				scene: this.scene,
				node,
				step: { order, seconds },
			});
		},

		moveToTop(node) {
			return this._reorderSteps({
				scene: this.scene,
				node,
				move: "top",
			});
		},

		moveUp(node) {
			return this._reorderSteps({
				scene: this.scene,
				node,
				move: "up",
			});
		},

		moveDown(node) {
			return this._reorderSteps({
				scene: this.scene,
				node,
				move: "down",
			});
		},

		moveToBottom(node) {
			return this._reorderSteps({
				scene: this.scene,
				node,
				move: "bottom",
			});
		},
	},
};
</script>
